import axios from 'axios';

function asArrayBuffer(u8: Uint8Array): ArrayBuffer {
  const ab = new ArrayBuffer(u8.byteLength);
  new Uint8Array(ab).set(u8);
  return ab;
}

async function sha256(data: string | Uint8Array): Promise<Uint8Array> {
  const enc = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  const dig = await crypto.subtle.digest('SHA-256', asArrayBuffer(enc as Uint8Array));
  return new Uint8Array(dig);
}

async function deriveKeyBytes(staticKey: string, suffix: string): Promise<CryptoKey> {
  const hash = await sha256(`${staticKey}:${suffix}`);
  return crypto.subtle.importKey('raw', asArrayBuffer(hash as Uint8Array), { name: 'AES-CBC' }, false, ['encrypt', 'decrypt']);
}

async function deriveIv(staticKey: string, suffix: string): Promise<Uint8Array> {
  const hash = await sha256(`iv:${staticKey}:${suffix}`);
  return hash.slice(0, 16);
}

function b64urlEncode(buf: Uint8Array): string {
  const b64 = btoa(String.fromCharCode(...buf));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64urlDecode(str: string): Uint8Array {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
  const bin = atob(b64 + pad);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

function randomSuffix(): string {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  const n = arr[0] % 100000000;
  return String(n).padStart(8, '0');
}

async function encryptJson(json: any, staticKey: string) {
  const suffix = randomSuffix();
  const key = await deriveKeyBytes(staticKey, suffix);
  const iv = await deriveIv(staticKey, suffix);
  const plain = new TextEncoder().encode(JSON.stringify(json));
  const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-CBC', iv: asArrayBuffer(iv as Uint8Array) }, key, asArrayBuffer(plain as Uint8Array));
  const data = b64urlEncode(new Uint8Array(cipherBuf));
  return { suffix, data };
}

async function decryptEnvelope(envelope: any, staticKey: string) {
  const { suffix, data } = envelope || {};
  if (!suffix || !data) return envelope;
  const key = await deriveKeyBytes(staticKey, suffix);
  const iv = await deriveIv(staticKey, suffix);
  const cipher = b64urlDecode(data);
  const plainBuf = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: asArrayBuffer(iv as Uint8Array) }, key, asArrayBuffer(cipher as Uint8Array));
  const text = new TextDecoder().decode(plainBuf);
  try { return JSON.parse(text); } catch { return text; }
}

const fallbackApiBase = (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8000/api') as string;

export const api = axios.create({
  baseURL: fallbackApiBase,
  headers: { 'Content-Type': 'application/json' },
});

const workerBase = (import.meta.env.VITE_WORKER_BASE_URL || 'https://worker.8atstreet.com') as string;
const projectName = (import.meta.env.VITE_PROJECT_NAME || '') as string;
const workerKey = (import.meta.env.VITE_WORKER_KEY || 'changeme') as string;

const enableWorker = (import.meta.env.VITE_ENABLE_WORKER || '') as string;
if (enableWorker === 'true' && projectName && workerBase) {
  fetch(`${workerBase}/${projectName}?type=api`)
    .then(res => res.json())
    .then(envelope => decryptEnvelope(envelope, workerKey))
    .then((data: { api?: string }) => {
      const apiUrl = data.api?.trim();
      if (apiUrl) {
        api.defaults.baseURL = apiUrl.replace(/\/+$/, '') + '/api';
      }
    })
    .catch(() => { });
}

api.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) (config.headers as any).Authorization = `Bearer ${token}`;
  const locked = (import.meta.env.VITE_NEXT_PUBLIC_IS_LOCKED || import.meta.env.VITE_IS_LOCKED || (typeof window !== 'undefined' ? (window as any).IS_LOCKED : undefined)) as string | undefined;
  const staticKey = (import.meta.env.VITE_NEXT_PUBLIC_API_STATIC_PARTIAL_KEY || 'changeme') as string;
  const m = (config.method || 'get').toLowerCase();
  // Skip encryption for FormData (file uploads)
  if (config.data instanceof FormData) {
    delete (config.headers as any)['Content-Type']; // Let axios set the correct boundary
    return config;
  }
  if (locked === 'true' && ['post', 'put', 'patch'].includes(m)) {
    const data = config.data ?? {};
    const isEnvelope = data && typeof data === 'object' && 'suffix' in data && 'data' in data;
    if (!isEnvelope) {
      return encryptJson(data, staticKey).then((env) => {
        config.data = env;
        (config.headers as any)['Content-Type'] = 'application/json';
        return config;
      });
    }
  }
  return config;
});

api.interceptors.response.use(async (resp) => {
  const locked = (import.meta.env.VITE_NEXT_PUBLIC_IS_LOCKED || import.meta.env.VITE_IS_LOCKED || (typeof window !== 'undefined' ? (window as any).IS_LOCKED : undefined)) as string | undefined;
  const staticKey = (import.meta.env.VITE_NEXT_PUBLIC_API_STATIC_PARTIAL_KEY || 'changeme') as string;
  const ct = resp.headers['content-type'] || '';
  if (locked === 'true' && ct.includes('application/json')) {
    const data = resp.data;
    const isEnvelope = data && typeof data === 'object' && 'suffix' in data && 'data' in data;
    if (isEnvelope) {
      resp.data = await decryptEnvelope(data, staticKey);
    }
  }
  return resp;
}, async (error) => {
  try {
    const locked = (import.meta.env.VITE_NEXT_PUBLIC_IS_LOCKED || import.meta.env.VITE_IS_LOCKED || (typeof window !== 'undefined' ? (window as any).IS_LOCKED : undefined)) as string | undefined;
    const staticKey = (import.meta.env.VITE_NEXT_PUBLIC_API_STATIC_PARTIAL_KEY || 'changeme') as string;
    const resp = error?.response;
    const ct = resp?.headers?.['content-type'] || '';
    if (locked === 'true' && resp && ct.includes('application/json')) {
      const data = resp.data;
      const isEnvelope = data && typeof data === 'object' && 'suffix' in data && 'data' in data;
      if (isEnvelope) {
        resp.data = await decryptEnvelope(data, staticKey);
      }
    }
  } catch { }
  return Promise.reject(error);
});

export default api;